import { BehaviorSubject, Subscription, filter, take } from 'rxjs';
import { Item, ShopCategory } from '../models/items.model';
import { MarketHistoriesResponse, MarketResponse } from '../models/ao-data-interfaces';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Location as AngularLocation, isPlatformServer } from '@angular/common';
import { JsonReaderService } from 'src/app/services/json-reader/json-reader.service';
import { AoDataGetterService } from 'src/app/services/ao-data-getter/ao-data-getter.service';
import { getAllRoyalMarketLocations } from '../constants/locations.constant';
import { ChangeDetectorRef, DestroyRef, Directive, inject, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { PriceType, SalesTax, SetupFee } from '../models/market.model';
import { PRICE_TYPES, getAllPriceTypes, getAllSalesTaxes, getAllSetupFees } from '../constants/market.constant';
import { AlbionImageService } from 'src/app/services/albion-image/albion-image.service';
import { Location } from '../models/location.model';
import { UserSettingsService } from 'src/app/services/user-settings/user-settings.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Directive()
export abstract class BasePage implements OnInit, OnDestroy {
  //CONTAINERS
  protected shopCategories: ShopCategory[] | null = null;
  protected items: Item[] | null = null;
  priceTypes: PriceType[] = getAllPriceTypes();

  locations: Location[] = getAllRoyalMarketLocations();

  setupFees: SetupFee[] = getAllSetupFees();

  salesTaxes: SalesTax[] = getAllSalesTaxes();
  selectedPriceType: PriceType = PRICE_TYPES.sell;

  //MARKET
  private pricesSubject: BehaviorSubject<MarketResponse[] | null> = new BehaviorSubject<MarketResponse[] | null>(null);
  protected prices$ = this.pricesSubject.asObservable();

  private historiesSubject: BehaviorSubject<MarketHistoriesResponse[] | null> = new BehaviorSubject<MarketHistoriesResponse[] | null>(null);
  protected histories$ = this.historiesSubject.asObservable();

  numberOfDaysForAverage: number = 15;
  selectedCities: Location[] = [];

  //SELECT VARIABLES
  protected selectedCategory: ShopCategory | null = null;
  protected selectedSubCategory: ShopCategory | null = null;
  protected selectedSubCategory2: ShopCategory | null = null;
  protected selectedTier: number | null = null;
  protected _selectedItem: Item | null = null;

  protected urlUniqueName!: string;
  protected isLoading: boolean = false;
  protected subscriptions: Subscription[] = [];

  destroyRef = inject(DestroyRef);

  constructor(
    protected route: ActivatedRoute,
    protected router: Router,
    protected urlLocation: AngularLocation,
    public jsonReaderService: JsonReaderService,
    protected aoDataGetter: AoDataGetterService,
    protected albionImageService: AlbionImageService,
    public userSettingsService: UserSettingsService,
    protected cdr: ChangeDetectorRef,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  ngOnInit(): void {
    this.jsonReaderService.ensureLoaded();
    this.subscriptions.push(
      this.userSettingsService.userServer$.subscribe((server) => {
        this.updatePrices(this.items ?? []);
        this.updateHistories(this.items ?? []);
      })
    );
    this.subscriptions.push(
      this.userSettingsService.userSelectedCities$.subscribe((selectedCities) => {
        this.selectedCities = selectedCities;
        this.cdr.markForCheck();
      })
    );
    this.subscriptions.push(
      this.userSettingsService.userLanguage$.subscribe((language) => {
        this.cdr.markForCheck();
      })
    );
    this.subscriptions.push(
      this.jsonReaderService.itemsData$.subscribe((items) => {
        this.items = this.filterItemsForSelect(items);
        this.cdr.markForCheck();
      })
    );

    this.subscriptions.push(
      this.jsonReaderService.shopCategoriesData$.subscribe((categories) => {
        this.shopCategories = this.filterShopCategoriesForSelect(categories);
        this.cdr.markForCheck();
      })
    );
    this.subscriptions.push(
      this.jsonReaderService.hasLoaded$.subscribe((hasLoaded) => {
        this.isLoading = !hasLoaded;
        if (hasLoaded) {
          this.setupFromUrl();
          this.cdr.markForCheck();
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.items = null;
    this.shopCategories = null;

    this.priceTypes = [];
    this.locations = [];
    this.setupFees = [];
    this.salesTaxes = [];
    this.selectedCities = [];

    this.pricesSubject.complete();
    this.historiesSubject.complete();

    this.pricesSubject = new BehaviorSubject<MarketResponse[] | null>(null);
    this.historiesSubject = new BehaviorSubject<MarketHistoriesResponse[] | null>(null);
  }

  protected abstract filterItemsForSelect(items: Item[]): Item[];
  protected abstract filterShopCategoriesForSelect(categories: ShopCategory[]): ShopCategory[];
  protected abstract filterItemsForPrices(items: Item[]): Item[];
  protected onItemSelectedActions?(): void;
  protected onEarlyItemSelectedActions?(): void;

  get selectedItem(): Item | null {
    return this._selectedItem;
  }
  set selectedItem(item: Item | null) {
    if (item !== undefined) {
      if (item !== this._selectedItem) {
        this._selectedItem = item;
        let currentPath = this.router.url.split('/')[1]; // Gets the base path segment
        this.router.navigate([currentPath, item?.uniqueName ? item.uniqueName : '']);
        if (this.onEarlyItemSelectedActions) this.onEarlyItemSelectedActions();
        if (item) {
          if (item.uniqueName != (this.route.snapshot.paramMap.get('uniqueName') ?? '')) {
            //this.urlLocation.go(`/${currentPath}/${this.selectedItem?.uniqueName ?? ''}`);
          }
          if (this.items) {
            this.updatePrices(this.filterItemsForPrices(this.items));
            this.updateHistories(this.filterItemsForPrices(this.items));
          }
        }
      }
      if (this.onItemSelectedActions) this.onItemSelectedActions();
    }
  }

  private setupFromUrl(): void {
    this.urlUniqueName = this.route.snapshot.paramMap.get('uniqueName') ?? '';
    if (this.urlUniqueName) {
      let urlItem = this.jsonReaderService.getItemFromIndex(this.urlUniqueName);
      if (urlItem && urlItem.uniqueName !== this.selectedItem?.uniqueName) {
        this.setSelectedItemFromUrl(urlItem);
        if (isPlatformServer(this.platformId)) {
          console.log(`Doing item setup for ${urlItem.uniqueName} on the server BASE PAGE.`);
        }
      }
    }
  }

  protected setSelectedItemFromUrl(item: Item): void {
    let urlTier = item.tier;
    if (urlTier && item.shopCategory && item.shopSubCategory1) {
      this.selectedCategory = item.shopCategory;
      this.selectedSubCategory = item.shopSubCategory1;
      this.selectedSubCategory2 = item.shopSubCategory2 ?? null;
      this.selectedTier = urlTier;
      this.selectedItem = item;
    }
  }

  protected updatePrices(items: Item[]): void {
    let filteredItems = this.filterItemsForPrices(items);
    if (filteredItems.length < 1) {
      this.pricesSubject.next(null);
      return;
    }
    this.aoDataGetter
      .getPrices(
        filteredItems.map((x) => x.uniqueName),
        getAllRoyalMarketLocations().map((x) => x.index.toString()),
        [1, 2, 3, 4, 5] //getting all qualities because aodata doesnt return all possible when there's no data
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((prices) => {
        // remove qualities that dont exist in the item
        prices = prices.filter((price) => price.qualityLevel.index <= (price.item.maxQualityLevel ?? 1));
        const sortedPrices = prices.sort((a, b) => (a.city?.displayName ?? '').localeCompare(b.city?.displayName ?? ''));
        this.pricesSubject.next(sortedPrices);
        this.cdr.detectChanges();
      });
  }

  protected updateHistories(items: Item[]): void {
    let filteredItems = this.filterItemsForPrices(items);
    if (filteredItems.length < 1) {
      this.historiesSubject.next(null);
      return;
    }

    let itemsIds: string[] = filteredItems.map((x) => x.uniqueName);

    this.aoDataGetter
      .getHistory(
        itemsIds,
        undefined,
        undefined,
        getAllRoyalMarketLocations().map((x) => x.index.toString()),
        [1, 2, 3, 4, 5], //needed cause Europe doesnt return all by default, dont need filter cause it only returns the ones that have data
        24
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((histories) => this.historiesSubject.next(histories));
  }

  getItemImageUrl(item: Item): string {
    return this.albionImageService.getItemImageUrl(item.uniqueName, 0, 75);
  }
}
